import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Swal from "sweetalert2";
import { selectAdminToken } from "../../admin/sessionSlice";
import { setBackButtonUrl } from "../uiSlice";
import { apiUrl } from "../../utils/apiUrl";
import { s3ThemeAssetUrl } from "../../utils/themeAssets";
import { getAuthBaseUserId, isMultiplaySession } from "../../utils/multiplay";
import "./feedback.css";

const FEEDBACK_WORDS = ["GOOD", "LOVED", "AWESOME", "AMAZING"];

function sameCell(a, b) {
  return a[0] === b[0] && a[1] === b[1];
}

export default function Feedback() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((s) => s.auth);
  const { data: themeData, status: themeStatus } = useSelector((s) => s.theme);
  const adminToken = useSelector(selectAdminToken);

  const [isMobile, setIsMobile] = useState(false);
  const [selected, setSelected] = useState([]);
  const [submittingWord, setSubmittingWord] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [statusLoaded, setStatusLoaded] = useState(false);

  const isDragging = useRef(false);
  const directionRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => setIsMobile(mq.matches);
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    if (!user) return;
    const base =
      (process.env.REACT_APP_BASE_URL || "").trim().replace(/\/+$/, "") || window.location.origin;
    dispatch(setBackButtonUrl(user.backButtonRedirect || base));
  }, [dispatch, user]);

  useEffect(() => {
    if (!user || !adminToken) {
      setStatusLoaded(true);
      return;
    }
    if (isMultiplaySession(user)) {
      setStatusLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: getAuthBaseUserId(user),
            sessionId: user.sessionId,
            organizationId: user.organizationId,
            isMultiplaySession: user.isMultiplaySession,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (cancelled) return;
        const raw = String(data.userFeedback || "").trim().toUpperCase();
        if (data.success && raw && FEEDBACK_WORDS.includes(raw)) {
          navigate("/leaderboard", { replace: true, state: { feedbackSubmitted: true } });
          return;
        }
      } catch (e) {
        console.error("spotGameStatus (feedback)", e);
      } finally {
        if (!cancelled) setStatusLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user, adminToken, navigate]);

  const saveFeedback = useCallback(
    async (word) => {
      if (!adminToken || submitting) return;
      setSubmitting(true);
      setSubmittingWord(word);
      await new Promise((resolve) => window.setTimeout(resolve, 140));
      try {
        const resp = await fetch(apiUrl("/saveUserFeedback"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: getAuthBaseUserId(user),
            sessionId: user.sessionId,
            organizationId: user.organizationId,
            userFeedback: word,
            isMultiplaySession: user.isMultiplaySession,
          }),
        });
        const data = await resp.json().catch(() => ({}));
        if (!resp.ok || data.success === false) {
          await Swal.fire({
            icon: "error",
            title: "Could not save",
            text: data.error || resp.statusText || "Try again later",
          });
          setSubmittingWord("");
          setSubmitting(false);
          return;
        }
        navigate("/leaderboard", { replace: true, state: { feedbackSubmitted: true } });
      } catch (e) {
        console.error("saveUserFeedback", e);
        await Swal.fire({ icon: "error", title: "Network error", text: String(e.message) });
        setSubmittingWord("");
        setSubmitting(false);
      }
    },
    [adminToken, submitting, user, navigate]
  );

  const checkFeedbackWord = useCallback(() => {
    setSelected((currentSelected) => {
      if (currentSelected.length === 0) return [];

      const wordIdx = currentSelected[0][0];
      if (!currentSelected.every(([w]) => w === wordIdx)) return [];

      const word = FEEDBACK_WORDS[wordIdx];
      if (currentSelected.length !== word.length) return [];

      const letters = currentSelected.map(([, i]) => word[i]).join("");
      const reverse = letters.split("").reverse().join("");
      if (letters === word || reverse === word) {
        saveFeedback(word);
      }
      return [];
    });
  }, [saveFeedback]);

  const start = useCallback(
    (wordIdx, letterIdx) => {
      if (submitting) return;
      isDragging.current = true;
      directionRef.current = null;
      setSelected([[wordIdx, letterIdx]]);
    },
    [submitting]
  );

  const move = useCallback(
    (wordIdx, letterIdx) => {
      if (!isDragging.current || submitting) return;
      setSelected((prev) => {
        if (prev.length === 0) return prev;
        const last = prev[prev.length - 1];
        const secondLast = prev[prev.length - 2];

        if (last[0] !== wordIdx) return prev;

        if (secondLast && sameCell([wordIdx, letterIdx], secondLast)) {
          if (prev.length === 2) directionRef.current = null;
          return prev.slice(0, -1);
        }
        if (prev.some((p) => sameCell(p, [wordIdx, letterIdx]))) return prev;

        const dl = letterIdx - last[1];
        if (Math.abs(dl) !== 1) return prev;

        if (!directionRef.current && prev.length === 1) {
          directionRef.current = [0, Math.sign(dl)];
        }
        if (directionRef.current) {
          const dirC = directionRef.current[1];
          if (Math.sign(dl) !== dirC) return prev;
        }
        return [...prev, [wordIdx, letterIdx]];
      });
    },
    [submitting]
  );

  const end = useCallback(() => {
    if (!isDragging.current) return;
    isDragging.current = false;
    directionRef.current = null;
    checkFeedbackWord();
  }, [checkFeedbackWord]);

  const handleTouchMove = useCallback(
    (e) => {
      const touch = e.touches[0];
      const el = document.elementFromPoint(touch.clientX, touch.clientY);
      if (el?.dataset?.pos) {
        const [w, l] = el.dataset.pos.split(",").map(Number);
        move(w, l);
      }
    },
    [move]
  );

  useEffect(() => {
    const onUp = () => {
      if (isDragging.current) end();
    };
    window.addEventListener("mouseup", onUp);
    return () => window.removeEventListener("mouseup", onUp);
  }, [end]);

  const isLetterSelected = (wordIdx, letterIdx) =>
    selected.some(([w, l]) => w === wordIdx && l === letterIdx);

  if (!user) {
    navigate("/login", { replace: true });
    return null;
  }

  const titleTextColor =
    themeData?.landing_page_title_color || themeData?.text_color || "#ffffff";

  const sceneBg = isMobile
    ? (themeData?.background_mob ? `url(${s3ThemeAssetUrl(themeData.background_mob)})` : "none")
    : (themeData?.background_desk ? `url(${s3ThemeAssetUrl(themeData.background_desk)})` : "none");

  if (themeStatus !== "succeeded" || !themeData || !statusLoaded) {
    return (
      <div className="quiz-loader-container">
        <div className="quiz-loader" />
      </div>
    );
  }

  return (
    <div
      className={`fb-page${isMobile ? " is-mobile" : ""}${submitting ? " fb-page--submitting" : ""}`}
      style={{
        backgroundImage: sceneBg,
        "--fb-grid-bg": themeData?.word_grid_bg_color || "#5f8de2",
        "--fb-grid-fg": themeData?.word_grid_text_color || "#ffffff",
        "--fb-active-bg": themeData?.grid_color || "#2196f3",
        "--fb-active-fg": themeData?.grid_active_text_color || "#ffffff",
        "--fb-title-color": titleTextColor,
      }}
      aria-label="Feedback"
    >
      <h1 className="fb-title" style={{ color: titleTextColor }}>
        HOW DID YOUR WORD HUNT FEEL?
      </h1>
      <p className="fb-hint">Drag across a word to submit your feedback</p>

      <div className="fb-word-grid" aria-label="Select one feedback word by dragging">
        {FEEDBACK_WORDS.map((word, wordIdx) => (
          <div key={word} className="fb-word-row" data-word={word}>
            {word.split("").map((ch, letterIdx) => {
              const active = isLetterSelected(wordIdx, letterIdx);
              const submittingThis =
                submitting && submittingWord === word && active;
              return (
                <div
                  key={letterIdx}
                  role="button"
                  tabIndex={submitting ? -1 : 0}
                  className={`fb-letter${active ? " is-active" : ""}${
                    submittingThis ? " is-submitting" : ""
                  }`}
                  data-pos={`${wordIdx},${letterIdx}`}
                  data-letter={ch}
                  aria-label={`${ch}, ${word}`}
                  onMouseDown={() => start(wordIdx, letterIdx)}
                  onMouseEnter={() => move(wordIdx, letterIdx)}
                  onMouseUp={end}
                  onTouchStart={() => start(wordIdx, letterIdx)}
                  onTouchMove={handleTouchMove}
                  onTouchEnd={end}
                >
                  {ch}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
